import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { AttachmentPreviewMethod } from 'owa-attachment-data';
import { SaveToCloudStatus } from 'owa-attachment-savetocloud';
import WacUrlInfo from 'owa-attachment-wac/lib/types/WacUrlInfo';
import { GetWacUrlForAttachmentIdError } from 'owa-attachment-wac/lib/utils/getWacUrlForAttachmentId';
import { ClientItemId } from 'owa-client-ids';
import { getExtensionWithoutDotFromFileName } from 'owa-file';
import fileExtensionToOfficeFileType from 'owa-file/lib/utils/fileExtensionToOfficeFileType';
import { getGuid } from 'owa-guid';
import { InstrumentationContext } from 'owa-mail-store';
import GetAttachmentTextPreviewReadMode from 'owa-service/lib/contract/GetAttachmentTextPreviewReadMode';
import {
    lazySxSStoreModule,
    onSxSChangeOrClose,
    PreviewPaneSelectionSource,
    SxSCustomization,
    sxsStore,
} from 'owa-sxs-store';
import { AutoHideEmailEvent, JsApiInfo, lazySxSDataModule } from 'owa-sxsdata';
import SxSReadingPaneStrategy from '../types/SxSReadingPaneStrategy';
import attachmentPreviewMethodToString from '../utils/attachmentPreviewMethodToString';
import insertSxSV2IntoDomHelper from '../utils/insertSxSV2IntoDomHelper';
import { isUserError, throwUserError } from '../utils/throwUserError';
import { initializeReadingPaneInSxS } from './internalActions';

export default async function previewLinkInSxS(
    wacUrlGetter: () => Promise<WacUrlInfo>,
    parentItemId: ClientItemId,
    selectionSource: PreviewPaneSelectionSource,
    readingPaneStrategy: SxSReadingPaneStrategy,
    instrumentationContext: InstrumentationContext,
    linkId: string,
    readOnly: boolean,
    customization: SxSCustomization = SxSCustomization.Mail
) {
    insertSxSV2IntoDomHelper.run();

    const wacUrlGetterPromise = wacUrlGetter();
    const sxsDataModule = await lazySxSDataModule.import();
    const sxsStoreModule = await lazySxSStoreModule.import();
    const { onPreviewOperationStart, onPreviewOperationFinished } = sxsStoreModule;
    const sxsViewState = sxsStore.viewState;
    const optionalParameters = {
        downloadUrl: null,
        downloadShouldOpenNewTab: false,
        useHighContrastCommandBar: false,
        id: null,
        fileName: null,
    };

    const perfDatapoint = logDatapoint(AttachmentPreviewMethod.Wac, selectionSource);
    const autoHideEventHandler = () => {
        perfDatapoint.addCustomData({
            autoHide: true,
        });
    };
    sxsViewState.eventManager.on(AutoHideEmailEvent, autoHideEventHandler);

    const currentPendingOperation = {
        id: `${new Date().getTime()}_${Math.floor(Math.random() * 1000)}`,
        attachmentId: null,
    };
    onPreviewOperationStart(currentPendingOperation);

    try {
        // wait if in edit mode
        await sxsStoreModule.canCloseSxS();

        throwIfPreviewOperationNotCurrent();
        initializeReadingPaneInSxS(
            readingPaneStrategy,
            selectionSource,
            parentItemId,
            instrumentationContext
        );
        sxsDataModule.setPreviewLoading(sxsViewState, '', optionalParameters);
        sxsStoreModule.showSxS(
            sxsStore.shouldShow ? null : customization,
            {
                previewMode: AttachmentPreviewMethod.Wac,
                attachmentId: null,
                selectionSource: selectionSource,
                isCloudy: true,
                saveToCloudStatus: SaveToCloudStatus.disabled,
                textPreviewReadMode: GetAttachmentTextPreviewReadMode.AutoDetect,
            },
            {
                editAllowed: false,
                itemId: parentItemId,
                isDraft: false,
            },
            linkId
        );

        onSxSChangeOrClose(linkId, readOnly);

        try {
            const wacUrlInfo = await wacUrlGetterPromise;
            throwIfPreviewOperationNotCurrent();

            if (wacUrlInfo.jsApiInfo) {
                const owaSessionId = getGuid();
                wacUrlInfo.jsApiInfo.owaSessionId = owaSessionId;
                optionalParameters.fileName = wacUrlInfo.jsApiInfo.fileName;
                addJsApiCustomDataToPerfDatapoint(perfDatapoint, wacUrlInfo.jsApiInfo);
            }

            sxsDataModule.setWacPreviewState(
                sxsViewState,
                wacUrlInfo.wacUrl,
                wacUrlInfo.jsApiInfo,
                wacUrlInfo.accessToken,
                wacUrlInfo.accessTokenTtl,
                wacUrlInfo.additionalParameters,
                wacUrlInfo.deprecatedTokenAndParameters,
                new Date().getTime(),
                readOnly,
                optionalParameters
            );
        } catch (error) {
            const typedError = error as GetWacUrlForAttachmentIdError;
            if (typedError?.errorMessage) {
                sxsDataModule.setPreviewError(sxsViewState, typedError.errorMessage);
                throw typedError.error;
            } else {
                sxsDataModule.setPreviewError(sxsViewState, error.message || '' + error);
                throw error;
            }
        }

        await sxsStoreModule.waitForPreviewLoadOrThrowError();
        throwIfPreviewOperationNotCurrent();
        onPreviewOperationFinished();

        perfDatapoint.end();
    } catch (error) {
        perfDatapoint.endWithError(
            isUserError(error) ? DatapointStatus.UserError : DatapointStatus.ServerError,
            error
        );
    } finally {
        sxsViewState.eventManager.off(AutoHideEmailEvent, autoHideEventHandler);
    }

    function throwIfPreviewOperationNotCurrent() {
        if (
            !sxsStore.pendingPreviewOperation ||
            currentPendingOperation.id !== sxsStore.pendingPreviewOperation.id
        ) {
            throwUserError('Preview changed before loading finished');
        }
    }
}

function logDatapoint(
    previewMethod: AttachmentPreviewMethod,
    actionSource: PreviewPaneSelectionSource
): PerformanceDatapoint {
    const perfDatapoint = new PerformanceDatapoint('PreviewLinkInSxSE2E');
    perfDatapoint.addCustomData({
        previewMethod: attachmentPreviewMethodToString(previewMethod),
        actionSource: PreviewPaneSelectionSource[actionSource],
    });
    return perfDatapoint;
}

function addJsApiCustomDataToPerfDatapoint(
    perfDatapoint: PerformanceDatapoint,
    jsApiInfo: JsApiInfo
) {
    const fileType = getExtensionWithoutDotFromFileName(jsApiInfo.fileName);
    perfDatapoint.addCustomData({
        useJsApi: 'true',
        officeFileType: fileExtensionToOfficeFileType(fileType),
        owaSessionId: jsApiInfo.owaSessionId,
    });
    perfDatapoint.addCosmosOnlyData(
        JSON.stringify({
            fileType: fileType?.substring(0, 4) || '',
        })
    );
}
